/**
 * FundRaisingChannelPanel
 * Detail panel for a single funding channel.
 * Tabs: Overview | Requirements | Documents | Applications | Activity
 */
import React, { useState, useEffect, useRef } from 'react';

const API = '/api/opo/fundraising';

const STATUS_COLORS = {
  active: '#10b981', 'check-intake': '#f59e0b', watchlist: '#6b7280', closed: '#ef4444',
  drafting: '#6366f1', review: '#f59e0b', ready: '#10b981', submitted: '#3b82f6',
  pending: '#8b5cf6', approved: '#10b981', rejected: '#ef4444',
};
const statusDot = (s) => (
  <span style={{ display: 'inline-block', width: 8, height: 8, borderRadius: '50%', background: STATUS_COLORS[s] || '#6b7280', marginRight: 6, flexShrink: 0 }} />
);
function fmt(n) { return n != null ? `$${Number(n).toLocaleString('en-CA')}` : '—'; }
function fmtDate(s) { return s ? new Date(s).toLocaleDateString('en-CA') : '—'; }

const CHANNEL_TABS = ['Overview', 'Requirements', 'Documents', 'Applications', 'Activity'];

export default function FundRaisingChannelPanel({ channelCode, onClose, onSelectApp }) {
  const [data, setData] = useState(null);
  const [activeTab, setActiveTab] = useState('Overview');
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState('');
  const [creatingApp, setCreatingApp] = useState(false);
  const [newAppName, setNewAppName] = useState('');
  const [newAppSlug, setNewAppSlug] = useState('');
  const fileInputRef = useRef();

  const load = () => {
    fetch(`${API}/channels/${channelCode}`)
      .then(r => r.json())
      .then(setData)
      .catch(() => {});
  };

  useEffect(() => { load(); }, [channelCode]);

  if (!data) return <div className="fr-loading">Loading channel…</div>;

  const { channel, applications, alerts, recent_runs, overview_md, requirements, documents, application_folders } = data;

  const handleUpload = async (e) => {
    const files = e.target.files;
    if (!files || !files.length) return;
    setUploading(true);
    setUploadMsg('');
    const form = new FormData();
    for (const f of files) form.append('files', f);
    try {
      const r = await fetch(`${API}/channels/${channelCode}/documents`, { method: 'POST', body: form });
      const d = await r.json();
      setUploadMsg(`Uploaded: ${(d.uploaded || []).map(u => u.saved_as).join(', ')}`);
      load();
    } catch (err) { setUploadMsg('Upload failed: ' + err.message); }
    setUploading(false);
  };

  const handleCreateApp = async () => {
    if (!newAppName || !newAppSlug) return;
    const r = await fetch(`${API}/applications`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ channel_code: channelCode, name: newAppName, slug: newAppSlug }),
    });
    if (r.ok) {
      const d = await r.json();
      setCreatingApp(false);
      setNewAppName('');
      setNewAppSlug('');
      load();
      if (onSelectApp) onSelectApp(d.application?.id);
    }
  };

  return (
    <div className="fr-channel-detail">
      <div className="fr-channel-detail-header">
        <h2 className="fr-channel-detail-name">{channel.name}</h2>
        <div className="fr-channel-detail-meta">
          {statusDot(channel.status)}
          <span className="fr-muted">{channel.status}</span>
          {channel.region && <span className="fr-badge fr-badge-region">{channel.region}</span>}
          {channel.funding_min_cad || channel.funding_max_cad
            ? <span className="fr-muted">CAD {fmt(channel.funding_min_cad)} – {fmt(channel.funding_max_cad)}</span>
            : null}
          {channel.repayable && <span className="fr-badge fr-badge-amber">Repayable</span>}
          {channel.requires_academic_partner && <span className="fr-badge fr-badge-info">Academic partner required</span>}
          {channel.fit_score != null && (
            <span className={`fr-fit-score fr-fit-${channel.fit_score >= 70 ? 'high' : channel.fit_score >= 40 ? 'mid' : 'low'}`}>
              Fit Score: {Math.round(channel.fit_score)}
            </span>
          )}
        </div>
        {channel.official_url && (
          <div style={{ marginTop: 6 }}>
            <a href={channel.official_url} target="_blank" rel="noopener noreferrer" className="fr-muted" style={{ fontSize: 12, color: 'var(--accent, #19efff)' }}>
              {channel.official_url}
            </a>
          </div>
        )}
      </div>

      <div className="fr-channel-tabs">
        {CHANNEL_TABS.map(t => (
          <button key={t} type="button" className={`fr-channel-tab ${activeTab === t ? 'active' : ''}`} onClick={() => setActiveTab(t)}>{t}</button>
        ))}
      </div>

      {/* Overview */}
      {activeTab === 'Overview' && (
        <div>
          {overview_md
            ? <pre className="fr-overview-md">{overview_md}</pre>
            : <div className="fr-panel"><p className="fr-muted">No overview yet. Run the Scout agent in seed mode to populate from {channel.official_url || 'the official URL'}.</p></div>
          }
          {channel.notes && (
            <div className="fr-panel" style={{ marginTop: 12 }}>
              <div className="fr-panel-title">Internal Notes</div>
              <p style={{ color: 'var(--text, #e8eaf6)', fontSize: 13, margin: 0 }}>{channel.notes}</p>
            </div>
          )}
          {channel.fit_rationale && (
            <div className="fr-panel" style={{ marginTop: 12 }}>
              <div className="fr-panel-title">Analyst Fit Rationale</div>
              <p style={{ color: 'var(--text, #e8eaf6)', fontSize: 13, margin: 0 }}>{channel.fit_rationale}</p>
            </div>
          )}
        </div>
      )}

      {/* Requirements */}
      {activeTab === 'Requirements' && (
        <div>
          {requirements
            ? <>
              {requirements.eligibility?.length > 0 && (
                <div className="fr-req-section fr-panel">
                  <h4>Eligibility</h4>
                  <ul>{requirements.eligibility.map((e, i) => <li key={i}>{e}</li>)}</ul>
                </div>
              )}
              {requirements.documents_required?.length > 0 && (
                <div className="fr-req-section fr-panel">
                  <h4>Documents Required</h4>
                  <ul>{requirements.documents_required.map((d, i) => <li key={i}>{d}</li>)}</ul>
                </div>
              )}
              {requirements.evaluation_criteria?.length > 0 && (
                <div className="fr-req-section fr-panel">
                  <h4>Evaluation Criteria</h4>
                  <ul>{requirements.evaluation_criteria.map((c, i) => <li key={i}>{c}</li>)}</ul>
                </div>
              )}
              {requirements.notes && (
                <div className="fr-panel">
                  <h4 style={{ fontSize: 11, textTransform: 'uppercase', color: 'var(--muted, #6b7280)', marginBottom: 8 }}>Notes</h4>
                  <p style={{ color: 'var(--text, #e8eaf6)', fontSize: 13, margin: 0 }}>{requirements.notes}</p>
                </div>
              )}
            </>
            : <div className="fr-panel"><p className="fr-muted">Requirements not yet populated. Run Scout agent in seed mode.</p></div>
          }
        </div>
      )}

      {/* Documents */}
      {activeTab === 'Documents' && (
        <div>
          <div className="fr-panel">
            <div className="fr-panel-title">Uploaded Documents ({(documents || []).length})</div>
            {(documents || []).length === 0
              ? <p className="fr-muted">No documents uploaded yet.</p>
              : <ul className="fr-doc-list">
                {(documents || []).map(d => (
                  <li key={d} className="fr-doc-item">
                    <span>📄</span>
                    <span>{d}</span>
                  </li>
                ))}
              </ul>
            }
            <div className="fr-upload-area" style={{ marginTop: 12 }}>
              <p>Upload documents (PDFs, letters, forms)</p>
              <input type="file" ref={fileInputRef} multiple style={{ display: 'none' }} onChange={handleUpload} />
              <button type="button" className="fr-btn" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
                {uploading ? 'Uploading…' : 'Choose Files'}
              </button>
              {uploadMsg && <p style={{ marginTop: 8, fontSize: 12, color: 'var(--accent, #19efff)' }}>{uploadMsg}</p>}
            </div>
          </div>

          {(application_folders || []).length > 0 && (
            <div className="fr-panel" style={{ marginTop: 12 }}>
              <div className="fr-panel-title">Application Folders on Disk</div>
              <ul className="fr-doc-list">
                {(application_folders || []).map(f => (
                  <li key={f} className="fr-doc-item"><span>📁</span><span>{f}</span></li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Applications */}
      {activeTab === 'Applications' && (
        <div>
          {!creatingApp
            ? <div style={{ marginBottom: 14 }}>
              <button type="button" className="fr-btn" onClick={() => setCreatingApp(true)}>+ New Application</button>
            </div>
            : <div className="fr-panel" style={{ marginBottom: 14 }}>
              <div className="fr-panel-title">New Application</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <input className="fr-input" placeholder="Name (e.g. IRAP Project Optilens 2026)" value={newAppName} onChange={e => setNewAppName(e.target.value)} style={{ width: '100%' }} />
                <input className="fr-input" placeholder="Slug (e.g. 2026_irap_optilens)" value={newAppSlug} onChange={e => setNewAppSlug(e.target.value.replace(/\s+/g, '_').toLowerCase())} style={{ width: '100%' }} />
                <div style={{ display: 'flex', gap: 8 }}>
                  <button type="button" className="fr-btn" onClick={handleCreateApp}>Create</button>
                  <button type="button" className="fr-btn-outline" onClick={() => setCreatingApp(false)}>Cancel</button>
                </div>
              </div>
            </div>
          }
          {(applications || []).length === 0
            ? <p className="fr-muted">No applications for this channel yet.</p>
            : (applications || []).map(a => (
              <div key={a.id} className="fr-pipeline-card" style={{ marginBottom: 8, cursor: 'pointer' }} onClick={() => onSelectApp?.(a.id)}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  {statusDot(a.status)}
                  <span className="fr-pipeline-card-name">{a.name}</span>
                  <span className={`fr-badge fr-badge-${a.status === 'approved' ? 'green' : a.status === 'rejected' ? 'red' : 'info'}`}>{a.status}</span>
                </div>
                {a.notes_md && <div className="fr-muted" style={{ fontSize: 12, marginTop: 4 }}>{a.notes_md.slice(0, 80)}</div>}
              </div>
            ))
          }
        </div>
      )}

      {/* Activity */}
      {activeTab === 'Activity' && (
        <div>
          <div className="fr-panel">
            <div className="fr-panel-title">Recent Agent Runs</div>
            {(recent_runs || []).length === 0
              ? <p className="fr-muted">No agent runs for this channel.</p>
              : (recent_runs || []).map(r => (
                <div key={r.id} className="fr-alert-row">
                  <span className="fr-badge fr-badge-region" style={{ textTransform: 'capitalize' }}>{r.agent_type}</span>
                  <span className={`fr-badge fr-badge-${r.status === 'completed' ? 'green' : r.status === 'failed' ? 'red' : 'info'}`}>{r.status}</span>
                  <span className="fr-muted">{fmtDate(r.started_at)}</span>
                  {r.summary_md && <span style={{ fontSize: 12, color: 'var(--text, #e8eaf6)', flex: 1 }}>{r.summary_md.slice(0, 120)}</span>}
                </div>
              ))
            }
          </div>
          <div className="fr-panel" style={{ marginTop: 12 }}>
            <div className="fr-panel-title">Alerts</div>
            {(alerts || []).length === 0
              ? <p className="fr-muted">No alerts for this channel.</p>
              : (alerts || []).slice(0, 10).map(a => (
                <div key={a.id} className="fr-alert-row">
                  <span className={`fr-badge fr-badge-${a.severity}`}>{a.severity}</span>
                  <span style={{ fontSize: 12, color: 'var(--text, #e8eaf6)', flex: 1 }}>{a.title || a.summary_md?.slice(0, 100)}</span>
                  <span className="fr-muted">{fmtDate(a.detected_at)}</span>
                  {!a.resolved_at && <span className="fr-badge fr-badge-red">Open</span>}
                </div>
              ))
            }
          </div>
        </div>
      )}
    </div>
  );
}
